import { makeAutoObservable, runInAction } from "mobx";
import CreateRmaService, {
    ICreateRMAResponse,
    IShouldRequestManagersApproval
} from "../../../services/api/create-rma-service";
import {rootContainer, TYPE} from "../../../services/container/root";
import OrderService from "../../../services/order-service";
import currentPortalStore from "../../../stores/current-portal-store";
import CreateRmaResponseStore from "./create-rma-response-store";
import OrderStore from "./order-store";
import CreateRmaSettingsStore from './create-rma-settings-store';
import RmaRequestStore, { ICreateRmaDto } from "./rma-request-store";
import currentAccountStore from "../../accounts/stores/current-account-store";
import managerApprovalModalStore from "../../../manager-approval-modal/manager-approval-modal-store";


export class CreateRmaMainStore {
	private _orderStore?: OrderStore;
	private _isSearching: boolean = false;
	private _requestStore?: RmaRequestStore;
	private _searchOrderName: string = '';
	private _searchError: string | null = null;
	private _isCreatingRma: boolean = false;
	private _showResponseModal: boolean = false;
	private _createRmaResponseStore?: CreateRmaResponseStore;

	private get orderService(): OrderService { return rootContainer.get<OrderService>(TYPE.OrderService); }
	private get createRmaService(): CreateRmaService { return rootContainer.get<CreateRmaService>(TYPE.CreateRmaService); }
	public get isSearching(): boolean {
		return this._isSearching || this._orderStore?.customer?.isLoading || !!this._orderStore?.customer?.isLoadingRmas;
	}
	public get searchError(): string | null { return this._searchError; }
	public get searchOrderName(): string { return this._searchOrderName; };
	public get orderStore(): OrderStore | undefined { return this._orderStore; }
	public get settings(): CreateRmaSettingsStore { return this._settingsStore; }
	public get rmaRequestStore(): RmaRequestStore | undefined { return this._requestStore; }
	public get showResponseModal(): boolean { return this._showResponseModal; }
	public get createRmaResponseStore(): CreateRmaResponseStore | undefined { return this._createRmaResponseStore; }

	public setSearchOrderName(value: string) {
		this._searchOrderName = value;
	}

	constructor(private _settingsStore: CreateRmaSettingsStore) {
		makeAutoObservable(this, {}, { autoBind: true });
	}

	public createRmaRequestStore() {
		if (!this._orderStore) {
			/** Cannot create RmaRequestStore without loaded order, this happens when swithcing C-RMA mode before order was loaded */
			return;
		}
		/** each time we create new instance we need to call dispose on old class, unsubscribe */
		this._destroyRequestStore();
		this._requestStore = new RmaRequestStore(this._orderStore, this);
	}

	public __destroy():void {
		this._destroyRequestStore();
	}

	private _destroyRequestStore(): void {
		this._requestStore?.__destroy();
		this._requestStore = undefined;
	}

	public async searchForOrder() {
		try {

			if (!this._searchOrderName.trim()) {
				return;
			}

			this._isSearching = true;
			/** when searchnig for order - we should destroy previous request store to prevent intermediate state when there are return items from previous order still on the screen */
			this._destroyRequestStore();

            const withEligibility = true;
            const useCache = false;
            const withRefunded = false;
			let response = await this.orderService.fetchOrder(this._searchOrderName, null, withEligibility, useCache, withRefunded);

			runInAction(() => {
				this._isSearching = false;
				if (response.error_message) {
					return this._setSearchError(response.error_message);
				}
				this._orderStore = new OrderStore(response, response?.eligibility);
				this._orderStore.loadCustomer();

				this.createRmaRequestStore();
				this._setSearchError(null);
			})

		} catch (e) {
			console.error(e);
			runInAction(() => {
				this._isSearching = false;
				this._setSearchError(this.getSearchErrorText());
			});
		}
	}

	private getSearchErrorText(): string {
		/** TODO: we need to show not found when the order actually not found */
		return 'Order ' + this._searchOrderName + ' was not found'
	}

	private _setSearchError(value: string | null) {
		this._searchError = value;
	}



	get isCreatingRma(): boolean { return this._isCreatingRma; }

    private _shouldRequestManagersApproval(response: ICreateRMAResponse | IShouldRequestManagersApproval): response is IShouldRequestManagersApproval {
        return 'request_manager_approval' in response;
    }

	private _shouldRequestCredentials(request: ICreateRmaDto): boolean {
		if(request?.is_auto_approved) {
            if (currentPortalStore.requireManagerApproval() &&
                !currentAccountStore.hasManagersApprovePermissions() &&
                currentAccountStore.hasPromptManagersApprovePermissions() &&
                this.rmaRequestStore?.eligibilityStore.isOutOfPolicy
            ) {
				return true;
            }
        }
		return false;
	}

	async createRma(forceRequestCredentials: boolean = false): Promise<void> {
        if(!this.rmaRequestStore){
            return;
        }

		this._isCreatingRma = true;

		await this.rmaRequestStore.balancePreviewStore.checkPreview();
		if (this.rmaRequestStore.balancePreviewStore.isCreateRmaBlocked) {
			runInAction(() => {
				this._isCreatingRma = false;
			});
			return;
		}

		const request = this.rmaRequestStore.requestModel();

		if (forceRequestCredentials || this._shouldRequestCredentials(request)) {
			const credentialsResult = await managerApprovalModalStore.collectCredentials();
			if(!credentialsResult.credentials) {
				runInAction(() => {
					this._isCreatingRma = false;
				});
				return;
			}
			request.manager_approval_credentials = credentialsResult.credentials;
		}

		try {

			const response = await this.createRmaService.createRma(request);

			runInAction(() => {
				this._isCreatingRma = false;

	            /** special case when server requested managers approval modal */
				if (this._shouldRequestManagersApproval(response)) {
					return this.createRma(true);
				}

				this._createRmaResponseStore = new CreateRmaResponseStore(response as ICreateRMAResponse);
				this._showResponseModal = true;
			});
		}
		catch (e) {
			console.error(e);
			runInAction(() => {
				this._isCreatingRma = false;
			});
		}
	}

	public hideResponseModal() {
		this._showResponseModal = false;
	}

}


export class CreateRmaMainStoreInstanceManager {
	private _instance?: CreateRmaMainStore;
    private _settingsStore: CreateRmaSettingsStore = new CreateRmaSettingsStore(this);

    constructor() {
        makeAutoObservable(this);
    }

	public createInstance(): void {
        runInAction(() => {
			/** always destroy previous store if exists before instantiating a new one */
			this._instance?.__destroy()
			this._instance = new CreateRmaMainStore(this._settingsStore);
        });
	}

    public getInstance(): CreateRmaMainStore|undefined {
		return this._instance;
	}

    async loadSettings() {
		this._settingsStore.load();
		currentPortalStore.load();
    }

	public voidInstance(): void {
        runInAction(() => {
			this._instance?.__destroy()
            this._instance = undefined;
        });
	}
}

const instanceManager =  new CreateRmaMainStoreInstanceManager();
export default instanceManager;
